import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Country } from "@somasi/shared";
import { api } from "@/integrations/aws/resources";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";
import { notify } from "@/lib/notify";
import { useAuth } from "@/lib/auth";

/**
 * Country picker over the maintained country list, searchable, with an inline
 * "add" for a jurisdiction that is not on it yet.
 *
 * A search that matches nothing offers to create what was typed, and carries the
 * text straight into the new-country form — the moment you discover a country is
 * missing is the moment you know its name, so retyping it would be busywork.
 *
 * The field stores the country's *name*, not its code, because that is what the
 * rows already hold: the column was free text before this list existed. A stored
 * value matching nothing in the list is still offered, so opening an older record
 * does not silently blank its country.
 *
 */

export const COUNTRIES_QUERY_KEY = ["countries"] as const;

export function useCountries() {
  return useQuery({
    queryKey: COUNTRIES_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await api.countries.list().all().eq("is_active", true).order("name");
      if (error) throw error;
      return data ?? [];
    },
    // Reference data: no need to refetch it on every dialog open.
    staleTime: 5 * 60 * 1000,
  });
}

const ALPHA_2 = /^[A-Z]{2}$/;

export function CountryField({
  value,
  onChange,
  label = "Country",
  required,
  id = "country",
  className = "col-span-full",
}: {
  value: string;
  /** The chosen country's name. */
  onChange: (country: string) => void;
  label?: string;
  /** Marks the label with the required asterisk; the caller still validates. */
  required?: boolean;
  id?: string;
  /**
   * How the field sits in its parent grid. The default gives it a whole row,
   * which is what the add panel wants; a field sharing a row with others passes
   * its own span instead, and the panel stacks to fit.
   */
  className?: string;
}) {
  const { canEdit } = useAuth();
  const qc = useQueryClient();
  const { data: countries = [], isLoading } = useCountries();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ code: "", name: "" });
  const codeRef = useRef<HTMLInputElement>(null);

  const byName = useMemo(() => {
    const m = new Map<string, Country>();
    for (const c of countries) m.set(c.name.toLowerCase(), c);
    return m;
  }, [countries]);

  // A country saved before this list existed, or since marked inactive.
  const unlisted = value && !byName.has(value.toLowerCase()) ? value : null;

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter(
      (c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().startsWith(q),
    );
  }, [countries, search]);

  // The code is the only field the typed name cannot supply, so it takes focus.
  useEffect(() => {
    if (adding) codeRef.current?.focus();
  }, [adding]);

  const create = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.countries.create({
        code: draft.code,
        name: draft.name.trim(),
      });
      if (error || !data) throw new Error(error?.message ?? "Could not add the country");
      return data;
    },
    onSuccess: (country) => {
      // Put the new country into the cached list before selecting it, so the
      // option it is being set to already exists. Waiting on a refetch first
      // would leave a window where the field holds a value with no matching
      // item, and a picker with nothing to show falls back to its placeholder.
      qc.setQueryData<Country[]>(COUNTRIES_QUERY_KEY, (previous = []) =>
        [...previous.filter((c) => c.id !== country.id), country].sort((a, b) =>
          a.name.localeCompare(b.name),
        ),
      );

      onChange(country.name);
      closeAdd();
      setOpen(false);
      notify.success(`${country.name} added and selected`);

      // Reconcile with the server in the background; the list is already right.
      void qc.invalidateQueries({ queryKey: COUNTRIES_QUERY_KEY });
    },
    onError: (error: Error) => notify.error(error),
  });

  function closeAdd() {
    setAdding(false);
    setDraft({ code: "", name: "" });
    setSearch("");
  }

  /** Opens the add form with whatever was searched for as the country's name. */
  function startAdd(name: string) {
    setDraft({ code: "", name: name.trim() });
    setAdding(true);
    setOpen(false);
  }

  function pick(country: Country) {
    onChange(country.name);
    setOpen(false);
    setSearch("");
  }

  const valid = ALPHA_2.test(draft.code) && !!draft.name.trim();

  return (
    // `@container` lets the add panel below size itself against this field
    // rather than the viewport, so it stacks in a narrow grid cell.
    <div className={cn("@container space-y-2", className)}>
      <Label htmlFor={id} required={required}>
        {label}
      </Label>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={isLoading}
            className="w-full justify-between font-normal"
          >
            <span className={cn(!value && "text-muted-foreground")}>
              {value || (isLoading ? "Loading…" : "Select a country")}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          {/* The list is filtered above, so cmdk's own matching is turned off. */}
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search countries…"
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty className="p-2">
                {canEdit && search.trim() ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full justify-start font-normal"
                    onClick={() => startAdd(search)}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create “{search.trim()}”
                  </Button>
                ) : (
                  <span className="text-sm text-muted-foreground">No country found.</span>
                )}
              </CommandEmpty>

              <CommandGroup>
                {value && (
                  <CommandItem
                    value="__clear__"
                    onSelect={() => {
                      onChange("");
                      setOpen(false);
                    }}
                  >
                    <X className="mr-2 h-4 w-4 opacity-50" />
                    <span className="text-muted-foreground">Clear</span>
                  </CommandItem>
                )}

                {unlisted && (
                  <CommandItem value={unlisted} onSelect={() => setOpen(false)}>
                    <Check className="mr-2 h-4 w-4" />
                    {unlisted}
                    <span className="ml-2 text-xs text-muted-foreground">not in list</span>
                  </CommandItem>
                )}

                {matches.map((c) => (
                  <CommandItem key={c.id} value={c.name} onSelect={() => pick(c)}>
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        c.name.toLowerCase() === value.toLowerCase() ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span className="flex-1">{c.name}</span>
                    <span className="text-xs text-muted-foreground">{c.code}</span>
                  </CommandItem>
                ))}
              </CommandGroup>

              {canEdit && search.trim() && matches.length > 0 && (
                <CommandGroup className="border-t">
                  <CommandItem value="__add__" onSelect={() => startAdd(search)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create “{search.trim()}”
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {adding && (
        <div className="space-y-2 rounded-md border bg-muted/40 p-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium">New country</p>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={closeAdd}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-2 @md:grid-cols-[6rem_1fr]">
            <Input
              ref={codeRef}
              aria-label="ISO code"
              placeholder="VG"
              maxLength={2}
              value={draft.code}
              onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
            />
            <Input
              aria-label="Country name"
              placeholder="British Virgin Islands"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </div>

          <p className="text-xs text-muted-foreground">
            Two-letter ISO country code and the country name.
          </p>

          {/* A nested form would submit the dialog it sits in, so this is a button. */}
          <Button
            type="button"
            size="sm"
            disabled={!valid || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Adding…" : "Add country"}
          </Button>
        </div>
      )}
    </div>
  );
}
